import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { FC, useEffect, useState } from 'react';
import { getSetsData, getSetsState } from 'selectors/sets';
import { getUrlParams } from 'helpers/utils';
import { getSets } from 'store/sets/actions';
import { getPagesAPI } from 'store/pages/actions';
import { StripeExpressCard } from './StripeExpressCard';
import { OathRecurringInterval } from 'store/pay/types';
import { IPage } from 'store/donatePage/types';
import Loader from 'components/Loader';
import { AUTOPILOT_CLIENT_ID } from 'helpers/donate';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { SET_QUERY_STRINGS } from 'constants/dbRecords';
import { AUTOPILOT_QUERY_STRINGS, MAX_CARD_COUNT } from './constants';

interface Props {
  donationAmount: number;
  page?: IPage;
  recurringInterval: OathRecurringInterval | 'one_time';
}

export const StripeCarousel: FC<Props> = ({ donationAmount, page, recurringInterval }) => {
  const { urlPartner } = getUrlParams();
  const dispatch = useAppDispatch();
  const { setsState, setsData } = useAppSelector(state => ({
    setsState: getSetsState(state),
    setsData: getSetsData(state),
  }));
  const [autopilotPage, setAutopilotPage] = useState<IPage | null>(null);
  const [isLoadingAutopilot, setIsLoadingAutopilot] = useState(false);
  const [isAutopilotError, setIsAutopilotError] = useState(false);

  const isAutopilot = page?.client_id === AUTOPILOT_CLIENT_ID;
  const isLoading = isAutopilot ? isLoadingAutopilot : !setsData?.pages && !setsState.error;
  const otherQueryStr = AUTOPILOT_QUERY_STRINGS.find(qs => qs !== urlPartner);
  const hasFetchError = isAutopilot ? isAutopilotError : !!setsState.error;

  const fetchAutopilotPage = async (queryStr: string) => {
    setIsLoadingAutopilot(true);
    setIsAutopilotError(false);
    try {
      const response = await getPagesAPI(queryStr);
      setAutopilotPage(response.data.pages?.[0] ?? null);
    } catch {
      setAutopilotPage(null);
      setIsAutopilotError(true);
    } finally {
      setIsLoadingAutopilot(false);
    }
  };

  const getIssues = (): IPage[] => {
    if (isAutopilot) {
      return autopilotPage ? [autopilotPage] : [];
    }
    if (!setsData?.pages) return [];
    return setsData.pages
      .filter(
        p =>
          p.query_str !== urlPartner && p.processing_redirect === ProcessingRedirectOptions.STRIPE
      )
      .slice(0, MAX_CARD_COUNT);
  };

  const handleRetry = () => {
    if (isAutopilot) {
      if (!otherQueryStr) return;
      fetchAutopilotPage(otherQueryStr);
    } else {
      dispatch(getSets(SET_QUERY_STRINGS.TOP_ISSUES));
    }
  };

  useEffect(() => {
    if (isAutopilot) {
      if (!otherQueryStr) return;
      fetchAutopilotPage(otherQueryStr);
    } else if (setsState?.data?.querystring !== SET_QUERY_STRINGS.TOP_ISSUES) {
      dispatch(getSets(SET_QUERY_STRINGS.TOP_ISSUES));
    }
  }, []);

  const issues = getIssues();
  if (!isLoading && !hasFetchError && issues.length === 0) return null;

  return (
    <div className="confirmation-background-container bg-oath-blue shadow-10">
      <div className="donate-again-container">
        <span className="h3-dtm color-inkBlue">Double Your Impact</span>
        <div className="donate-again-cards-row">
          {isLoading ? (
            <div className="loader-center">
              <Loader width={60} height={60} />
            </div>
          ) : hasFetchError ? (
            <div className="fetch-error-container">
              <span className="body-text mb16 color-errorRed">
                There was an error loading your recommendations.
              </span>
              <button className="button-brand bg-errorRed" onClick={handleRetry}>
                Try Again
              </button>
            </div>
          ) : (
            issues.map(issue => (
              <StripeExpressCard
                key={issue.id}
                donationAmount={donationAmount}
                issue={issue}
                recurringInterval={recurringInterval}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
